import { graphql, Link } from "gatsby";
import React, { useState } from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import Layout from "../components/common/layout";
import Seo from "../components/common/seo";
import CustomLink from "../components/common/Link";

const BlogsByCategory = ({ data, pageContext }) => {
  const { sanityCategory, allSanityBlog } = data;
  const { category } = pageContext;
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredBlogs, setFilteredBlogs] = useState(allSanityBlog.nodes);

  // Filter blogs based on search term
  const handleSearch = (term) => {
    setSearchTerm(term);
    if (term === "") {
      setFilteredBlogs(allSanityBlog.nodes);
    } else {
      const filtered = allSanityBlog.nodes.filter(
        (blog) =>
          blog.blogTitle.toLowerCase().includes(term.toLowerCase()) ||
          blog.overview?.toLowerCase().includes(term.toLowerCase()) ||
          blog.blogSubTitle?.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredBlogs(filtered);
    }
  };

  const BlogCard = ({ blog }) => {
    const image = getImage(blog.thumbnailImage?.image?.asset);

    return (
      <article className="group bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden transform hover:-translate-y-1">
        <Link to={`/blog/${blog.slug.current}`} className="block">
          {/* Blog Image */}
          <div className="relative h-48 bg-gray-100 overflow-hidden">
            {image ? (
              <GatsbyImage
                image={image}
                alt={blog.thumbnailImage?.altText || blog.blogTitle}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
              </div>
            )}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300"></div>
          </div>

          {/* Blog Content */}
          <div className="p-6">
            {/* Subtitle */}
            {blog.blogSubTitle && (
              <div className="text-sm text-logo1 font-medium uppercase tracking-wider mb-2">
                {blog.blogSubTitle}
              </div>
            )}

            {/* Title */}
            <h3 className="text-xl font-bold text-black mb-3 group-hover:text-gray-800 transition-colors duration-200 line-clamp-2">
              {blog.blogTitle}
            </h3>

            {/* Overview */}
            {blog.overview && (
              <p className="text-gray-600 leading-relaxed mb-4 line-clamp-3">
                {blog.overview}
              </p>
            )}

            {/* Author & Date */}
            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              {blog.author && (
                <span className="font-medium">{blog.author.name}</span>
              )}
              {blog.publishedAt && (
                <time dateTime={blog.publishedAt}>
                  {new Date(blog.publishedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </time>
              )}
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {blog.tags.slice(0, 2).map((tag) => (
                  <Link
                    key={tag.slug?.current}
                    to={`/blog/tag/${tag.slug?.current}`}
                    className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded-full transition-colors duration-200"
                  >
                    #{tag.name}
                  </Link>
                ))}
                {blog.tags.length > 2 && (
                  <span className="px-2 py-1 text-xs text-gray-500">
                    +{blog.tags.length - 2}
                  </span>
                )}
              </div>
            )}

            {/* Read More Link */}
            <div className="flex items-center text-logo1 font-medium group-hover:text-opacity-80 transition-colors duration-200">
              <span className="text-sm uppercase tracking-wider">
                Read More
              </span>
              <svg
                className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </Link>
      </article>
    );
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-white py-16 lg:py-24">
        <div className="container mx-auto px-4 max-w-6xl mt-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Category Badge */}
            <div className="mb-6">
              <span className="inline-flex items-center px-4 py-2 bg-logo1 text-black rounded-full text-sm font-medium uppercase tracking-wider">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
                    clipRule="evenodd"
                  />
                </svg>
                {sanityCategory?.name || category}
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-6">
              {sanityCategory?.name || category} Articles
            </h1>

            {sanityCategory?.description && (
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
                {sanityCategory.description}
              </p>
            )}

            {/* Search Bar */}
            <div className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search within these articles..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full px-4 py-3 pl-12 pr-4 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-logo1 focus:border-transparent transition-all duration-200"
                />
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Results Info */}
          <div className="mb-8 flex items-center justify-between">
            <p className="text-gray-600">
              {searchTerm ? (
                <>
                  Showing {filteredBlogs.length} result
                  {filteredBlogs.length !== 1 ? "s" : ""} for "{searchTerm}"
                </>
              ) : (
                <>
                  {filteredBlogs.length} article
                  {filteredBlogs.length !== 1 ? "s" : ""} in{" "}
                  {sanityCategory?.name || category}
                </>
              )}
            </p>

            {/* Back to all articles */}
            <CustomLink
              cls="flex items-center text-logo1 hover:text-opacity-80 font-medium transition-colors duration-200"
              data={{ buttonSlug: { current: "/blog" } }}
            >
              <svg
                className="mr-2 w-4 h-4"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              All Articles
            </CustomLink>
          </div>

          {/* Blog Grid */}
          {filteredBlogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog) => (
                <BlogCard key={blog.slug.current} blog={blog} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <svg
                className="mx-auto w-16 h-16 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33C7.76 10.906 9.72 10 12 10s4.24.906 6.08 2.67z"
                />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No articles found
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm
                  ? `No articles in "${
                      sanityCategory?.name || category
                    }" match "${searchTerm}". Try a different search term.`
                  : `No articles have been published in the "${
                      sanityCategory?.name || category
                    }" category yet.`}
              </p>
              {searchTerm && (
                <button
                  onClick={() => handleSearch("")}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-logo1 hover:text-opacity-80 transition-colors duration-200"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export const query = graphql`
  query BlogsByCategory($slug: String!) {
    sanityCategory(slug: { current: { eq: $slug } }) {
      name
      slug {
        current
      }
    }
    allSanityBlog(
      filter: {
        categories: { elemMatch: { slug: { current: { eq: $slug } } } }
        status: { eq: "published" }
      }
      sort: { publishedAt: DESC }
    ) {
      nodes {
        blogTitle
        slug {
          current
        }
        blogSubTitle
        author {
          name
        }
        publishedAt
        thumbnailImage {
          altText
          image {
            asset {
              gatsbyImageData(
                layout: CONSTRAINED
                formats: [WEBP, AUTO]
                placeholder: BLURRED
              )
            }
          }
        }
        overview
        featured
        categories {
          name
          slug {
            current
          }
        }
        tags {
          name
          slug {
            current
          }
        }
      }
    }
  }
`;

export function Head({ location, data }) {
  return <Seo data={data?.sanityCategory?.seo} location={location} />;
}

export default BlogsByCategory;
